import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/lib/pricing";
import { fmtDate } from "@/lib/quoteText";
import AiDialog from "@/components/invoices/AiDialog";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;
const daysOver = (due) => Math.floor((Date.now() - new Date(due).getTime()) / 86400000);

function Row({ inv, shop, onPaid, onSharePdf, onEmail }) {
  const [ai, setAi] = useState(false);
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-rose-500/20 bg-slate-950/40 px-4 py-3" data-testid={`overdue-row-${inv.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-heading font-bold truncate">{inv.company || inv.customer_name || "Walk-in"}</span>
          <span className="font-mono-num text-xs text-sky-300">{inv.invoice_no}</span>
          <span className="rounded-full bg-rose-500/15 border border-rose-500/40 text-rose-300 text-[10px] font-bold px-2 py-0.5" data-testid={`overdue-days-${inv.id}`}>{daysOver(inv.due_date)}d overdue</span>
        </div>
        <div className="text-xs text-slate-400 mt-0.5 truncate">{[inv.plate, inv.vehicle].filter(Boolean).join(" · ")} · due {fmtDate(inv.due_date)}{inv.customer_phone ? ` · ${inv.customer_phone}` : ""}</div>
      </div>
      <div className="font-mono-num font-black text-rose-300 text-lg sm:text-right">{formatMoney(shop.sym, inv.total)}</div>
      <div className="flex gap-2 shrink-0">
        <Button size="sm" onClick={() => setAi(true)} data-testid={`overdue-ai-${inv.id}`} className="bg-violet-600 hover:bg-violet-500 text-white h-8"><Sparkles className="w-3.5 h-3.5 mr-1" /> AI reminder</Button>
        <Button size="sm" variant="outline" onClick={() => onPaid(inv)} data-testid={`overdue-paid-${inv.id}`} className="h-8 border-slate-700 bg-slate-900/60 text-slate-200 hover:bg-slate-800"><Check className="w-3.5 h-3.5 mr-1" /> Paid</Button>
      </div>
      <AiDialog inv={inv} shop={shop} open={ai} onOpenChange={setAi} defaultPurpose="reminder" onSharePdf={(t) => onSharePdf(inv, t)} onEmail={(t) => onEmail(inv, t)} />
    </div>
  );
}

export default function OverdueInvoices({ shop, onSharePdf, onEmail, onChange }) {
  const [items, setItems] = useState([]);
  useEffect(() => { axios.get(`${API}/invoices`, { params: { status: "overdue" } }).then(({ data }) => setItems(data)).catch(() => {}); }, []);
  const markPaid = async (inv) => {
    await axios.patch(`${API}/invoices/${inv.id}/status`, { status: "paid" });
    toast.success(`${inv.invoice_no} marked as paid`);
    setItems((l) => l.filter((x) => x.id !== inv.id));
    onChange?.();
  };
  if (!items.length) return null;
  const total = items.reduce((s, x) => s + Number(x.total || 0), 0);
  return (
    <section className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-4 sm:p-5 space-y-3" data-testid="overdue-invoices">
      <div className="flex items-center gap-2 flex-wrap">
        <AlertTriangle className="w-4 h-4 text-rose-400" />
        <h3 className="font-heading font-bold">Overdue invoices <span className="font-zh text-rose-300/70 text-sm">逾期发票</span></h3>
        <span className="text-xs text-slate-400 ml-auto" data-testid="overdue-summary">{items.length} overdue · <span className="font-mono-num text-rose-300">{formatMoney(shop.sym, total)}</span></span>
      </div>
      <div className="space-y-2">{items.map((inv) => <Row key={inv.id} inv={inv} shop={shop} onPaid={markPaid} onSharePdf={onSharePdf} onEmail={onEmail} />)}</div>
    </section>
  );
}
